import { UnfurlItem } from '@lib/external/unfurl';
import { blake3 } from 'hash-wasm';

// Constants for unique ID generation
const UNIQUE_ID_LENGTH = 64;
const DEFAULT_EXTRACTOR = 'youtube';

// Global cache for generated unique IDs to avoid re-computation
// Keyed by "extractor:id" to avoid collisions across platforms
const hashCache = new Map<string, string>();

/**
 * Minimal interface for items that can be checked for duplicates.
 * Only requires the uniqueId property that duplicate detection actually needs.
 *
 * Types that implement DuplicateCheckable:
 * - UnfurlItem (from @lib/external/unfurl)
 * - ExternalMediaItem (from @services/Upload/DuplicateDetectionService)
 * - UploadedMediaItem (from @services/Upload/DuplicateDetectionService)
 * - YouTubePlaylistItem (from @stores/MomentSelection/YouTubePlaylistSelection)
 *
 * Behavior when uniqueId is missing:
 * - Items without uniqueId are considered unique (not duplicates)
 * - isMediaDuplicate() returns false for items without uniqueId
 * - filterOutDuplicates() keeps items without uniqueId (they pass through)
 * - This allows items that haven't been processed yet to be included
 */
export interface DuplicateCheckable {
  uniqueId?: string;
}

/**
 * Generate a unique ID for media items using blake3 hash.
 * Matches the backend's uniqueId generation: blake3(`${extractor}:${id}`, 64).
 *
 * @param id - The platform-specific media ID (e.g. YouTube video ID, Frame.io asset UUID)
 * @param extractor - The backend extractor name (e.g. 'youtube', 'next.frame.io', 'generic')
 * @returns A 64-character hex string unique ID
 * @throws Error if hash generation fails
 */
export async function generateUniqueId(
  id: string,
  extractor: string = DEFAULT_EXTRACTOR,
): Promise<string> {
  const cacheKey = `${extractor}:${id}`;
  if (hashCache.has(cacheKey)) {
    return hashCache.get(cacheKey)!;
  }

  const hash = await blake3(cacheKey, UNIQUE_ID_LENGTH);
  hashCache.set(cacheKey, hash);
  return hash;
}

/**
 * Generate hashes for media items
 * Only computes hashes for items that haven't been processed before
 * @param items - Array of media items to generate hashes for
 * @returns Promise resolving to items with uniqueId added
 * @throws Error if hash generation fails
 */
export async function generateHashesForItems(items: UnfurlItem[]): Promise<UnfurlItem[]> {
  try {
    const itemsWithHashes = await Promise.all(
      items.map(async (item) => {
        // Check if we already have a uniqueId for this item
        if (item.uniqueId) {
          return item;
        }

        // Only compute hash for items that don't have uniqueId yet
        const uniqueId = await generateUniqueId(item.id, item.extractor);
        return {
          ...item,
          uniqueId,
        };
      }),
    );
    return itemsWithHashes;
  } catch (error) {
    console.error('Failed to generate hashes for items:', error);
    // Return items without uniqueId if hash generation fails
    return items.map((item) => ({ ...item, uniqueId: undefined }));
  }
}

/**
 * Incrementally generate hashes for new items, preserving existing items with hashes
 * This is more efficient when adding new items to an already processed dataset
 * @param newItems - Array of new media items to generate hashes for
 * @param existingItemsWithHashes - Array of already processed items with hashes
 * @returns Promise resolving to combined array with all items having hashes
 * @throws Error if hash generation fails
 */
export async function generateHashesForItemsIncremental(
  newItems: UnfurlItem[],
  existingItemsWithHashes: UnfurlItem[] = [],
): Promise<UnfurlItem[]> {
  try {
    // Create a set of existing IDs for quick lookup
    const existingIds = new Set(existingItemsWithHashes.map((item) => item.id));

    // Filter out items that already have hashes
    const itemsToHash = newItems.filter((item) => !existingIds.has(item.id));

    // Only generate hashes for new items
    const newItemsWithHashes = await generateHashesForItems(itemsToHash);

    // Combine existing and new items with hashes
    return [...existingItemsWithHashes, ...newItemsWithHashes];
  } catch (error) {
    console.error('Failed to incrementally generate hashes for items:', error);
    // Return existing items plus new items without uniqueId if hash generation fails
    return [
      ...existingItemsWithHashes,
      ...newItems.map((item) => ({ ...item, uniqueId: undefined })),
    ];
  }
}

/**
 * Get selected media items from a list of all items and selected IDs
 */
export function getSelectedData(allItems: UnfurlItem[], selectedIds: string[]): UnfurlItem[] {
  return selectedIds
    .map((id) => allItems.find((item) => item.id === id))
    .filter((item) => item !== undefined) as UnfurlItem[];
}

/**
 * Get selected URLs with names for upload processing
 * Filters out items that don't have a valid URL
 */
export function getSelectedUrls(
  allItems: UnfurlItem[],
  selectedIds: string[],
): { url: string; name: string; userMetadata?: Record<string, any>; uniqueId?: string }[] {
  return getSelectedData(allItems, selectedIds)
    .filter((item) => item.url) // Skip items without a valid URL
    .map((item) => {
      const result: {
        url: string;
        name: string;
        userMetadata?: Record<string, any>;
        uniqueId?: string;
      } = {
        url: item.url,
        name: item.title,
        userMetadata: {
          ...item.userMetadata,
          ...(item.description && { description: item.description }),
          ...(item.channel && { channel: item.channel }),
          // Intentionally NOT written to userMetadata:
          // - `duration` / `publishedAt`: the backend owns both as first-class
          //   fields (`metadata.video.duration` is the source of truth;
          //   `publishedAt` is top-level), so duplicating them caused erroneous
          //   data (a ~1ms duration discrepancy, a redundant publishedAt) that
          //   threw off downstream agents on GDrive-ingested media.
          // - `thumbnail`: once processed, the thumbnail URL is derived from
          //   libraryId/mediaId (`{PREVIEW_URL}/{lib}/{media}/media/thumbnail.webp`,
          //   gated on `capabilities.preview`), so the stored copy was only a
          //   pre-processing fallback — not worth persisting into userMetadata.
        },
        ...(item.uniqueId && { uniqueId: item.uniqueId }),
      };

      // Check if it's a short from the isShorts property (already in unfurlData response)
      if ('isShorts' in item && item.isShorts === true) {
        result.userMetadata = { ...result.userMetadata, isYoutubeShort: true };
      }

      return result;
    });
}

/**
 * Check if all items are selected
 */
export function isAllSelected(allItems: UnfurlItem[], selectedIds: string[]): boolean {
  if (allItems.length === 0) return false;
  const allIds = allItems.map((item) => item.id);
  return allIds.every((id) => selectedIds.includes(id));
}

/**
 * Check if some (but not all) items are selected
 */
export function isPartiallySelected(allItems: UnfurlItem[], selectedIds: string[]): boolean {
  const allIds = allItems.map((item) => item.id);
  const selectedCount = allIds.filter((id) => selectedIds.includes(id)).length;
  return selectedCount > 0 && selectedCount < allIds.length;
}

/**
 * Get all item IDs for select all functionality
 */
export function getAllItemIds(allItems: UnfurlItem[]): string[] {
  return allItems.map((item) => item.id);
}

/**
 * Check if a media item is a duplicate (already exists in library)
 * @param item - The media item to check (only needs uniqueId property)
 * @param existingUniqueIds - Record of existing unique IDs in the library
 * @returns True if the item is a duplicate, false if unique or if uniqueId is missing
 *
 * Behavior:
 * - Returns false if item.uniqueId is undefined/null (item is considered unique)
 * - Returns true if item.uniqueId exists in existingUniqueIds (item is a duplicate)
 * - Returns false if item.uniqueId exists but not in existingUniqueIds (item is unique)
 */
export function isMediaDuplicate(
  item: DuplicateCheckable,
  existingUniqueIds: Record<string, string>,
): boolean {
  return item.uniqueId ? item.uniqueId in existingUniqueIds : false;
}

/**
 * Check if a media item is a duplicate by ID
 * @param itemId - The ID of the media item to check
 * @param allItems - Array of all media items (only needs id and uniqueId properties)
 * @param existingUniqueIds - Record of existing unique IDs in the library
 * @returns True if the item is a duplicate
 */
export function isMediaDuplicateById<T extends DuplicateCheckable & { id: string } = UnfurlItem>(
  itemId: string,
  allItems: T[],
  existingUniqueIds: Record<string, string>,
): boolean {
  const item = allItems.find((item) => item.id === itemId);
  return item ? isMediaDuplicate(item, existingUniqueIds) : false;
}

/**
 * Filter out duplicate items from a list of media items
 * @param items - Array of media items to filter (only needs uniqueId property)
 * @param existingUniqueIds - Record of existing unique IDs in the library
 * @returns Array of non-duplicate items (items without uniqueId are kept)
 *
 * Behavior:
 * - Items without uniqueId are kept (considered unique, not filtered out)
 * - Items with uniqueId that exists in existingUniqueIds are filtered out (duplicates)
 * - Items with uniqueId that doesn't exist in existingUniqueIds are kept (unique)
 */
export function filterOutDuplicates<T extends DuplicateCheckable>(
  items: T[],
  existingUniqueIds: Record<string, string>,
): T[] {
  return items.filter((item) => !isMediaDuplicate(item, existingUniqueIds));
}

/**
 * Filter out duplicate IDs from a list of item IDs
 * @param itemIds - Array of item IDs to filter
 * @param allItems - Array of all media items
 * @param existingUniqueIds - Record of existing unique IDs in the library
 * @returns Array of non-duplicate item IDs
 */
export function filterOutDuplicateIds(
  itemIds: string[],
  allItems: UnfurlItem[],
  existingUniqueIds: Record<string, string>,
): string[] {
  return itemIds.filter((id) => !isMediaDuplicateById(id, allItems, existingUniqueIds));
}

/**
 * Get all duplicate items from a list of media items
 * @param items - Array of media items to check (only needs uniqueId property)
 * @param existingUniqueIds - Record of existing unique IDs in the library
 * @returns Array of duplicate items
 */
export function getDuplicateItems<T extends DuplicateCheckable>(
  items: T[],
  existingUniqueIds: Record<string, string>,
): T[] {
  return items.filter((item) => isMediaDuplicate(item, existingUniqueIds));
}

/**
 * Get selected items from data array based on selected IDs
 * @param data - Array of all media items
 * @param selectedIds - Array of selected item IDs
 * @returns Array of selected media items
 */
export function getSelectedItems(data: UnfurlItem[], selectedIds: string[]): UnfurlItem[] {
  return data.filter((item) => selectedIds.includes(item.id));
}

/**
 * Get count of non-duplicate selected items
 * @param data - Array of all media items
 * @param selectedIds - Array of selected item IDs
 * @param existingUniqueIds - Record of existing unique IDs in the library
 * @returns Count of selected items that are not duplicates
 */
export function getNonDuplicateSelectedCount(
  data: UnfurlItem[],
  selectedIds: string[],
  existingUniqueIds: Record<string, string>,
): number {
  const selectedItems = getSelectedItems(data, selectedIds);
  return selectedItems.filter((item) => !isMediaDuplicate(item, existingUniqueIds)).length;
}

/**
 * Filter out duplicate IDs from a list using a duplicate checker function
 * @param ids - Array of item IDs to filter
 * @param data - Array of all media items (must have id property)
 * @param isDuplicate - Function to check if an item is a duplicate
 * @returns Array of non-duplicate item IDs
 */
export function filterNonDuplicates<T extends DuplicateCheckable & { id: string } = UnfurlItem>(
  ids: string[],
  data: T[],
  isDuplicate: (item: T) => boolean,
): string[] {
  return ids.filter((id) => {
    const item = data.find((item) => item.id === id);
    return item ? !isDuplicate(item) : false;
  });
}

/**
 * Check if an item exists in data by ID
 * @param id - Item ID to check
 * @param data - Array of all media items
 * @returns The item if found, undefined otherwise
 */
export function findItemById(id: string, data: UnfurlItem[]): UnfurlItem | undefined {
  return data.find((item) => item.id === id);
}
